import { term } from '../terms';
// Interactive controls for the Louder component library: dropdowns, selects, filter pills,
// the range filter, the date range picker, steppers, switches and form validation.
// Motion follows the Bencho blocks Alberto picked (fold dropdown, range, form error,
// liquid toggle); every control works from the keyboard as well as the pointer.

import { uiFloat, uiUnfloat } from './ui-float';

const EDGE = 8; // gap kept between a floating panel and the screen edge
const FOCUSABLE = '[role=menuitem],[role=option],[role=menuitemcheckbox],[role=menuitemradio],input:not([type=hidden]),button:not([disabled]),a[href]';

/**
 * Menu or popover under a trigger. Esc closes and returns focus, a click outside closes,
 * arrow keys walk the items, Enter or Space picks. A click on a .ui-pi closes the panel
 * unless the item (or the panel) has data-keep.
 *
 * <div x-data="uiDropdown()"> <button x-ref="trigger" x-on:click="toggle()">…</button>
 *   <div x-ref="panel" x-show="open" …>…</div></div>
 */
export function uiDropdown(opts = {}) {
    let host = null; // the dropdown's own element (see uiTable)
    return {
        open: false,
        up: false,
        init() {
            host = this.$el;
            const t = this.triggerEl();
            t?.setAttribute('aria-haspopup', opts.role ?? 'menu');
            t?.setAttribute('aria-expanded', 'false');
            this.$watch('open', (v) => {
                this.triggerEl()?.setAttribute('aria-expanded', v ? 'true' : 'false');
                if (v) this.$nextTick(() => this.place());
                else if (this.$refs.panel) uiUnfloat(this.$refs.panel);
            });
        },
        triggerEl() {
            return this.$refs.trigger ?? (host ?? this.$root).querySelector('.ui-dd-t button, .ui-dd-t [tabindex], .ui-dd-t a');
        },
        toggle() { this.open ? this.close() : this.show(); },
        show(focusFirst = false) {
            this.open = true;
            this.$nextTick(() => {
                if (!this.open) return;
                const items = this.items();
                const pick = this.$refs.panel?.querySelector('input[type=search],input[type=text]') ?? (focusFirst ? items[0] : null);
                pick?.focus({ preventScroll: true });
            });
        },
        close(refocus = false) {
            if (!this.open) return;
            this.open = false;
            if (refocus) this.triggerEl()?.focus();
        },
        items() {
            return [...(this.$refs.panel?.querySelectorAll(FOCUSABLE) ?? [])].filter((el) => el.offsetParent !== null);
        },
        place() {
            const panel = this.$refs.panel;
            if (!panel || (host ?? this.$root).hasAttribute('data-noflip')) return;
            const r = (host ?? this.$root).getBoundingClientRect();
            const vw = document.documentElement.clientWidth, vh = window.innerHeight;
            // Every panel opens in the top layer, fixed under its trigger, so no card, table or page edge can cut it
            // off: it slides sideways to stay on screen, opens upwards when there's no room below, and follows page and
            // table scrolls. Inside something that scrolls sideways it closes once the trigger scrolls out of view.
            const row = (host ?? this.$root).closest('[data-dd-fixed], .ui-tw');
            const box = row && getComputedStyle(row).overflowX !== 'visible' ? row.getBoundingClientRect() : { left: 0, right: vw };
            if (r.right < box.left || r.left > box.right || r.bottom < 0 || r.top > vh) return this.close();
            uiFloat(panel);
            Object.assign(panel.style, { position: 'fixed', left: '0px', right: 'auto', top: '0px', bottom: 'auto', maxWidth: `calc(100vw - ${EDGE * 2}px)`, maxHeight: '' }); // measure unsqueezed
            const w = panel.offsetWidth, h = panel.offsetHeight;
            const below = vh - r.bottom - 6 - EDGE, above = r.top - 6 - EDGE;
            this.up = h > below && above > below;
            const left = Math.max(EDGE, Math.min(panel.classList.contains('ui-right') ? r.right - w : r.left, vw - w - EDGE));
            // taller than the room on either side (a long list on a phone): it scrolls inside
            Object.assign(panel.style, { left: left + 'px', top: this.up ? 'auto' : r.bottom + 6 + 'px', bottom: this.up ? vh - r.top + 6 + 'px' : 'auto', maxHeight: h > (this.up ? above : below) ? Math.max(120, this.up ? above : below) + 'px' : '' });
            if (!this.follow) {
                let frame = 0;
                this.follow = () => { if (this.open && !frame) frame = requestAnimationFrame(() => { frame = 0; if (this.open) this.place(); }); };
                window.addEventListener('scroll', this.follow, { passive: true, capture: true });
                window.addEventListener('resize', this.follow);
            }
        },
        triggerKey(e) {
            if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
                e.preventDefault();
                this.show(true);
            }
        },
        panelKey(e) {
            const list = this.items();
            const i = list.indexOf(document.activeElement);
            if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
            else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
            else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
            else if ((e.key === 'Enter' || e.key === ' ') && document.activeElement?.matches('.ui-pi')) { e.preventDefault(); document.activeElement.click(); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(true); }
            else if (e.key === 'Tab') this.close();
        },
        panelClick(e) {
            const item = e.target.closest('.ui-pi');
            if (item && !item.hasAttribute('data-keep') && !this.$refs.panel.hasAttribute('data-keep')) this.close(true);
        },
    };
}

/**
 * Select after Bencho's fold dropdown: the choice stays in the button, the other options
 * spring out below it, and picking one slides it up into the button as the rest fold away.
 * x-data="uiSelect(options, value)"; `value` is x-modelable. options: [{ value, label, icon? }]
 */
export function uiSelect(options = [], initial = null) {
    return {
        options,
        value: initial ?? options[0]?.value ?? null,
        open: false,
        hot: null,
        order: [],
        get current() { return this.options.find((o) => String(o.value) === String(this.value)) ?? this.options[0]; },
        init() { this.order = this.options.map((o) => o.value); },
        show() {
            if (this.open) return;
            this.order = [this.value, ...this.options.map((o) => o.value).filter((v) => String(v) !== String(this.value))];
            this.hot = this.value;
            this.open = true;
        },
        slot(v) { return this.order.findIndex((x) => String(x) === String(v)); },
        choose(v) {
            this.value = v;
            this.open = false;
            this.hot = null;
            this.$refs.button?.focus();
        },
        key(e) {
            if (!this.open) {
                if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); this.show(); }
                return;
            }
            const at = this.order.findIndex((x) => String(x) === String(this.hot));
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.open = false; }
            else if (e.key === 'ArrowDown') { e.preventDefault(); this.hot = this.order[Math.min(this.order.length - 1, at + 1)]; }
            else if (e.key === 'ArrowUp') { e.preventDefault(); this.hot = this.order[Math.max(0, at - 1)]; }
            else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.choose(this.hot ?? this.value); }
            else if (e.key === 'Tab') this.open = false;
        },
    };
}

/**
 * Filter pill with its popover. Inactive it is a grey pill with a plus; set, it is a tinted
 * chip with the value and a clear ×. `selected` is x-modelable (an array).
 * options: [{ value, label, flag?, meta?, group? }]; `multiple` picks several; `search` filters the list.
 */
export function uiFilter(cfg = {}) {
    return {
        ...uiDropdown({ role: 'listbox' }),
        options: cfg.options ?? [],
        multiple: !!cfg.multiple,
        selected: [...(cfg.selected ?? [])],
        query: '',
        get shown() {
            const q = this.query.trim().toLowerCase();
            return q ? this.options.filter((o) => o.label.toLowerCase().includes(q)) : this.options;
        },
        get summary() {
            const labels = this.options.filter((o) => this.selected.includes(o.value)).map((o) => o.label);
            if (!labels.length) return '';
            return labels.length <= 2 ? labels.join(', ') : `${labels[0]} +${labels.length - 1}`;
        },
        isOn(v) { return this.selected.includes(v); },
        pick(v) {
            if (this.multiple) {
                this.selected = this.isOn(v) ? this.selected.filter((x) => x !== v) : [...this.selected, v];
            } else {
                this.selected = this.isOn(v) ? [] : [v];
                this.close(true);
            }
            this.$dispatch('ui-filter', { name: cfg.name, selected: this.selected });
        },
        clear() {
            this.selected = [];
            this.query = '';
            this.$dispatch('ui-filter', { name: cfg.name, selected: [] });
        },
        groupState(group) {
            const vals = this.options.filter((o) => o.group === group).map((o) => o.value);
            const on = vals.filter((v) => this.selected.includes(v)).length;
            return on === 0 ? false : on === vals.length ? true : 'mix';
        },
        pickGroup(group) {
            const vals = this.options.filter((o) => o.group === group).map((o) => o.value);
            const all = this.groupState(group) === true;
            this.selected = all ? this.selected.filter((v) => !vals.includes(v)) : [...new Set([...this.selected, ...vals])];
        },
    };
}

/**
 * Range filter after Bencho's range: the spread of the data as bars, the chosen bars stand up in
 * ink, and the two values are tags you drag along the line (arrow keys move the focused one,
 * Shift by five steps). x-data="uiRange({ min, max, lo, hi, step, bins, gap })"; lo / hi are x-modelable.
 * `log` spaces the scale logarithmically (video counts).
 */
export function uiRange(cfg = {}) {
    const min = cfg.min ?? 0;
    const max = cfg.max ?? 100;
    const log = !!cfg.log;
    const toP = (v) => (log ? Math.log10(Math.max(v, 1)) / Math.log10(max) : (v - min) / (max - min));
    const fromP = (p) => (log ? 10 ** (p * Math.log10(max)) : min + p * (max - min));
    return {
        lo: cfg.lo ?? min,
        hi: cfg.hi ?? max,
        bins: cfg.bins ?? [],
        drag: null,
        get a() { return toP(this.lo); },
        get b() { return toP(this.hi); },
        get near() { return (this.b - this.a) * (this.$root.offsetWidth - 40) < 84; },
        fmt(v) {
            const f = cfg.format === 'compact' ? Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(v) : Math.round(v).toLocaleString('en-US');
            return `${cfg.prefix ?? ''}${f}${cfg.suffix ?? ''}${v >= max && cfg.plus ? '+' : ''}`;
        },
        inside(i) { const mid = (i + 0.5) / this.bins.length; return mid >= this.a - 1e-9 && mid <= this.b + 1e-9; },
        binH(c) { const m = Math.max(1, ...this.bins); return Math.max(0.1, c / m); },
        snap(v) {
            const step = cfg.step ?? 1;
            if (log) { const mag = 10 ** Math.floor(Math.log10(Math.max(v, 1))); return Math.min(max, Math.max(min, Math.round(v / mag) * mag)); }
            return Math.min(max, Math.max(min, Math.round(v / step) * step));
        },
        fromX(x) {
            const r = this.$refs.line.getBoundingClientRect();
            return fromP(Math.min(1, Math.max(0, (x - r.left - 20) / (r.width - 40))));
        },
        put(which, raw) {
            const gap = cfg.gap ?? 0;
            const v = this.snap(raw);
            if (which === 'lo') this.lo = Math.min(v, log ? this.hi : this.hi - gap);
            else this.hi = Math.max(v, log ? this.lo : this.lo + gap);
        },
        down(e) {
            if (e.button !== 0) return;
            e.preventDefault();
            const at = this.fromX(e.clientX);
            const which = Math.abs(toP(at) - this.a) <= Math.abs(toP(at) - this.b) ? 'lo' : 'hi';
            this.drag = which;
            this.put(which, at);
            (which === 'lo' ? this.$refs.lo : this.$refs.hi).focus({ preventScroll: true });
            const move = (ev) => this.put(which, this.fromX(ev.clientX));
            const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                this.drag = null;
                this.$dispatch('ui-range', { lo: this.lo, hi: this.hi });
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        },
        key(which, e) {
            const dir = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
            if (!dir) return;
            e.preventDefault();
            const cur = which === 'lo' ? this.lo : this.hi;
            const steps = e.shiftKey ? 5 : 1;
            const next = log ? fromP(toP(cur) + (dir * steps) / 20) : cur + dir * steps * (cfg.step ?? 1);
            this.put(which, next);
        },
    };
}

/**
 * Date range picker: presets on the left, a month grid on the right; the first click sets the
 * start, the second the end (in either order), hovering previews the range.
 * x-data="uiDateRange({ start: 'YYYY-MM-DD', end, max })"; start / end are x-modelable.
 */
export function uiDateRange(cfg = {}) {
    const iso = (d) => d.toISOString().slice(0, 10);
    const parse = (s) => (s ? new Date(`${s}T00:00:00Z`) : null);
    const today = parse(cfg.max) ?? new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()));
    return {
        ...uiDropdown({ role: 'dialog' }),
        start: cfg.start ?? null,
        end: cfg.end ?? null,
        picking: false,
        hover: null,
        month: parse(cfg.end ?? cfg.start) ?? today,
        presets: [['Last 7 days', 7], ['Last 30 days', 30], ['Last 90 days', 90], ['This year', 'year']],
        get label() {
            if (!this.start) return cfg.placeholder ?? 'Any time';
            const f = (s) => parse(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
            return this.end && this.end !== this.start ? `${f(this.start)} → ${f(this.end)}` : f(this.start);
        },
        get title() { return this.month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }); },
        get days() {
            const y = this.month.getUTCFullYear();
            const m = this.month.getUTCMonth();
            const first = new Date(Date.UTC(y, m, 1));
            const lead = (first.getUTCDay() + 6) % 7;
            const count = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
            return [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => iso(new Date(Date.UTC(y, m, i + 1))))];
        },
        shift(n) { this.month = new Date(Date.UTC(this.month.getUTCFullYear(), this.month.getUTCMonth() + n, 1)); },
        future(d) { return d > iso(today); },
        state(d) {
            if (!d) return '';
            const s = this.start;
            const e = this.picking ? this.hover ?? this.start : this.end;
            const [lo, hi] = [s, e].sort();
            if (d === s || d === e) return 'ui-edge';
            return lo && hi && d > lo && d < hi ? 'ui-in' : '';
        },
        click(d) {
            if (!d || this.future(d)) return;
            if (!this.picking) { this.start = d; this.end = null; this.picking = true; return; }
            [this.start, this.end] = [this.start, d].sort();
            this.picking = false;
            this.$dispatch('ui-dates', { start: this.start, end: this.end });
        },
        preset(p) {
            const end = new Date(today);
            const start = p === 'year' ? new Date(Date.UTC(end.getUTCFullYear(), 0, 1)) : new Date(end.getTime() - (p - 1) * 864e5);
            this.start = iso(start);
            this.end = iso(end);
            this.month = end;
            this.picking = false;
            this.$dispatch('ui-dates', { start: this.start, end: this.end });
            this.close(true);
        },
        dayNum(d) { return d ? +d.slice(8) : ''; },
    };
}

/** Stepper with Back / Next; `current` is 1-based and x-modelable. */
export function uiSteps(cfg = {}) {
    return {
        steps: cfg.steps ?? [],
        current: cfg.current ?? 1,
        next() { if (this.current < this.steps.length) this.current++; },
        back() { if (this.current > 1) this.current--; },
        go(n) { if (n < this.current) this.current = n; },
    };
}

/**
 * Switch after Bencho's liquid toggle: the thumb stretches along its travel as it crosses, and it
 * can be dragged (it flips as it passes the middle). `on` is x-modelable.
 */
export function uiToggle(initial = false, disabled = false) {
    return {
        on: !!initial,
        moving: false,
        grip: null,
        flip() {
            if (disabled) return;
            this.on = !this.on;
            this.moving = true;
            clearTimeout(this._t);
            this._t = setTimeout(() => { this.moving = false; }, 420);
        },
        down(e) {
            if (disabled) return;
            this.grip = { x: e.clientX, moved: false };
            this.$root.setPointerCapture?.(e.pointerId);
        },
        move(e) {
            if (!this.grip) return;
            const r = this.$root.getBoundingClientRect();
            if (Math.abs(e.clientX - this.grip.x) > 3) this.grip.moved = true;
            if (!this.grip.moved) return;
            const past = e.clientX - r.left > r.width / 2;
            if (past !== this.on) this.flip();
        },
        up() {
            this.dragged = !!this.grip?.moved;
            this.grip = null;
        },
        /** Clicks, Enter, Space and a click on its label all arrive here; a drag has already flipped it. */
        click() {
            if (this.dragged) { this.dragged = false; return; }
            this.flip();
        },
    };
}

/**
 * Form validation after Bencho's form error: on submit every bad field turns its edge red, shakes
 * once and unfolds the reason beneath it; the message folds away as soon as the value is right.
 * Rules live on the inputs: data-rule="required|email|min:3|max:80|number|positive|tiktok".
 */
export function uiForm(cfg = {}) {
    const RULES = {
        required: (v) => (v.trim() ? null : 'This is required'),
        email: (v) => (!v || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? null : "That doesn't look like an email"),
        number: (v) => (!v || !Number.isNaN(Number(v)) ? null : 'Use numbers only'),
        positive: (v) => (!v || Number(v) >= 0 ? null : "This can't be negative"),
        tiktok: (v) => (!v || /tiktok\.com\/(music|@[^/]+\/video)\//i.test(v) || /^\d{15,20}$/.test(v.trim()) ? null : `Paste a TikTok ${term('sound')} or video link`),
        min: (v, n) => (!v || v.trim().length >= +n ? null : `At least ${n} characters`),
        max: (v, n) => (v.trim().length <= +n ? null : `At most ${n} characters`),
    };
    return {
        errors: {},
        sent: false,
        busy: false,
        check(input) {
            const rules = (input.dataset.rule ?? '').split('|').filter(Boolean);
            for (const r of rules) {
                const [name, arg] = r.split(':');
                const msg = RULES[name]?.(input.value ?? '', arg);
                if (msg) return msg;
            }
            return null;
        },
        fields() { return [...this.$root.querySelectorAll('[data-rule]')]; },
        input(e) {
            const el = e.target;
            if (!el.dataset?.rule || !this.errors[el.name]) return;
            if (!this.check(el)) this.errors = { ...this.errors, [el.name]: null };
        },
        error(name) { return this.errors[name] ?? null; },
        async submit() {
            const errors = {};
            this.fields().forEach((el) => { const m = this.check(el); if (m) errors[el.name] = m; });
            this.errors = errors;
            const bad = this.fields().filter((el) => errors[el.name]);
            bad.forEach((el) => {
                const box = el.closest('.ui-inp') ?? el;
                box.removeAttribute('data-shake');
                void box.offsetWidth;
                box.setAttribute('data-shake', '');
            });
            if (bad.length) { bad[0].focus(); return; }
            this.busy = true;
            await new Promise((r) => setTimeout(r, cfg.delay ?? 700));
            this.busy = false;
            this.sent = true;
            this.$dispatch('ui-toast', { tone: 'ok', title: cfg.done ?? 'Saved', text: cfg.doneText ?? '' });
            setTimeout(() => { this.sent = false; }, 1800);
        },
    };
}

/**
 * Table over server-rendered rows: sort, search, filter, paginate, select (with a bulk bar), expand and
 * remove, all in place. Each body row carries data-row="id" and data-v='{"score": 893, "title": "…"}'
 * (the values to sort and search on); an expanded detail row carries data-detail="id".
 * x-data="uiTable({ sort: 'per_day', dir: 'desc', per: 8, search: ['title', 'author'] })"
 */
export function uiTable(cfg = {}) {
    // Its own element, fixed at init: `$root` would be a nested component's root when a dialog or menu calls in.
    let host = null;
    let self = null; // the table's own data: methods called from a nested menu or dialog would otherwise resolve names there first
    const me = (ctx) => self ?? ctx;
    return {
        sort: cfg.sort ?? null,
        dir: cfg.dir ?? 'desc',
        per: cfg.per ?? 10,
        page: 1,
        q: '',
        filter: {},
        selected: [],
        open: [],
        removed: [],
        rows: [],
        init() {
            host = this.$el;
            self = Alpine.$data(this.$el);
            me(this).rows = [...host.querySelectorAll('tbody tr[data-row]')].map((tr) => ({ id: tr.dataset.row, v: JSON.parse(tr.dataset.v || '{}') }));
            this.$watch('q', () => { me(this).page = 1; me(this).apply(); });
            this.$watch('filter', () => { me(this).page = 1; me(this).apply(); });
            me(this).apply();
        },
        get matching() {
            const q = me(this).q.trim().toLowerCase();
            return me(this).rows.filter((r) => !me(this).removed.includes(r.id)
                && (!q || (cfg.search ?? ['title']).some((k) => String(r.v[k] ?? '').toLowerCase().includes(q)))
                && Object.entries(me(this).filter).every(([k, vals]) => !vals?.length || vals.includes(String(r.v[k]))));
        },
        get sorted() {
            if (!me(this).sort) return me(this).matching;
            const s = me(this).sort;
            const m = me(this).dir === 'asc' ? 1 : -1;
            return [...me(this).matching].sort((a, b) => {
                const x = a.v[s];
                const y = b.v[s];
                if (x == null) return 1;
                if (y == null) return -1;
                return (typeof x === 'string' ? x.localeCompare(y) : x - y) * m;
            });
        },
        get last() { return Math.max(1, Math.ceil(me(this).matching.length / me(this).per)); },
        get pageRows() { return me(this).sorted.slice((me(this).page - 1) * me(this).per, me(this).page * me(this).per); },
        get pageIds() { return me(this).pageRows.map((r) => r.id); },
        get allOnPage() {
            const on = me(this).pageIds.filter((id) => me(this).selected.includes(id)).length;
            return on === 0 ? false : on === me(this).pageIds.length ? true : 'mix';
        },
        sortBy(key) {
            if (me(this).sort === key) me(this).dir = me(this).dir === 'desc' ? 'asc' : 'desc';
            else { me(this).sort = key; me(this).dir = typeof me(this).rows[0]?.v[key] === 'string' ? 'asc' : 'desc'; }
            me(this).apply();
        },
        ariaSort(key) { return me(this).sort === key ? (me(this).dir === 'asc' ? 'ascending' : 'descending') : 'none'; },
        go(p) { if (p >= 1 && p <= me(this).last) { me(this).page = p; me(this).apply(); } },
        isSel(id) { return me(this).selected.includes(id); },
        toggleRow(id) { me(this).selected = me(this).isSel(id) ? me(this).selected.filter((x) => x !== id) : [...me(this).selected, id]; },
        togglePage() {
            const ids = me(this).pageIds;
            me(this).selected = me(this).allOnPage === true ? me(this).selected.filter((id) => !ids.includes(id)) : [...new Set([...me(this).selected, ...ids])];
        },
        clearSel() { me(this).selected = []; },
        toggleOpen(id) { me(this).open = me(this).open.includes(id) ? me(this).open.filter((x) => x !== id) : [...me(this).open, id]; me(this).apply(); },
        removeSelected() {
            const n = me(this).selected.length;
            me(this).removed = [...me(this).removed, ...me(this).selected];
            me(this).selected = [];
            if (me(this).page > me(this).last) me(this).page = me(this).last;
            me(this).apply();
            this.$dispatch('ui-toast', { tone: 'ok', title: `${n} ${n === 1 ? 'row' : 'rows'} removed`, text: 'Demo only: reload to bring them back.' });
        },
        csv() {
            const rows = me(this).rows.filter((r) => me(this).selected.includes(r.id));
            const keys = cfg.csv ?? Object.keys(rows[0]?.v ?? {});
            const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const text = [keys.join(','), ...rows.map((r) => keys.map((k) => esc(r.v[k])).join(','))].join('\n');
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
            a.download = cfg.filename ?? 'export.csv';
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            this.$dispatch('ui-toast', { tone: 'ok', title: `Exported ${rows.length} rows`, text: a.download });
        },
        /** Put the visible rows in order and hide the rest (rows are real server-rendered markup). */
        apply() {
            const body = host?.querySelector('tbody');
            if (!body) return;
            const show = new Set(me(this).pageIds);
            const order = me(this).sorted.map((r) => r.id);
            const before = new Map([...body.querySelectorAll('tr[data-row]')].map((tr) => [tr.dataset.row, tr.getBoundingClientRect().top]));
            order.forEach((id) => {
                const tr = body.querySelector(`tr[data-row="${CSS.escape(id)}"]`);
                if (!tr) return;
                body.appendChild(tr);
                const detail = body.querySelector(`tr[data-detail="${CSS.escape(id)}"]`);
                if (detail) body.appendChild(detail);
            });
            body.querySelectorAll('tr[data-row]').forEach((tr) => { tr.hidden = !show.has(tr.dataset.row); });
            body.querySelectorAll('tr[data-detail]').forEach((tr) => { tr.hidden = !show.has(tr.dataset.detail) || !me(this).open.includes(tr.dataset.detail); });
            body.querySelectorAll('tr[data-row]:not([hidden])').forEach((tr) => {
                const dy = (before.get(tr.dataset.row) ?? 0) - tr.getBoundingClientRect().top;
                if (dy && Math.abs(dy) < 600) tr.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], { duration: 260, easing: 'cubic-bezier(.22,1,.36,1)' });
            });
            const empty = host.querySelector('[data-empty]');
            if (empty) empty.hidden = me(this).matching.length > 0;
        },
    };
}

/** Modal dialog: focus moves in and stays in (Tab cycles), the page stops scrolling, focus returns on close. */
export function uiDialog() {
    return {
        open: false,
        back: null,
        show() {
            this.back = document.activeElement;
            this.open = true;
            document.documentElement.style.overflow = 'hidden';
            focusWhenShown(this.$root, () => this.$root.querySelector('input:not([type=hidden]),textarea,[role=combobox],.ui-dlg button:not(.ui-ibtn)') ?? this.$root.querySelector('button'));
        },
        close() {
            if (!this.open) return;
            this.open = false;
            document.documentElement.style.overflow = '';
            this.back?.focus?.({ preventScroll: true });
        },
        trap(e) {
            const list = [...this.$root.querySelectorAll('a[href],button:not([disabled]),input:not([type=hidden]),textarea,[tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
            if (!list.length) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        },
    };
}

/**
 * Focus an element inside `box` once `box` is actually showing: x-show with a transition makes it visible a frame or
 * two after `open` turns true, and focus() on a hidden element does nothing.
 */
export function focusWhenShown(box, pick, tries = 20) {
    const go = (n) => {
        const el = pick();
        if (el && box.getClientRects().length > 0 && getComputedStyle(box).display !== 'none') {
            el.focus({ preventScroll: true });
            if (document.activeElement === el || n <= 0) return;
        }
        if (n > 0) requestAnimationFrame(() => go(n - 1));
    };
    requestAnimationFrame(() => go(tries));
}
